import * as React from "react"

import {
  getAuthSession,
  useGetAuthSession,
} from "@boundless/bl-api-client/allauth/auth"
import {
  AuthenticationResponseStatus,
  type AuthenticationResponse,
} from "@boundless/bl-api-client/allauth/models"
import mixpanel from "mixpanel-browser"

import { GenericErrorComponent } from "@/components/error/GenericErrorComponent"
import { PendingComponent } from "@/components/error/PendingComponent"
import { getAuthClient, type ErrorType } from "@/lib/clients"
import { store } from "@/lib/store"
import { isRateLimitedError, redirectTo } from "@/lib/utils"

/**
 * @summary Narrowing function to Authentication Error (401)
 */
function isAuthenticationResponse(
  error: ErrorType<AuthenticationResponse | unknown>,
): error is ErrorType<AuthenticationResponse> {
  return error.response?.status === AuthenticationResponseStatus.NUMBER_401
}

/**
 * @summary Initialize the Authentication for the whole application.
 *
 * @desc This is used to fetch the initial authentication status from the
 * backend before loading anything else from the application.
 *
 * The authentication status is stored in the global state, since it's hard to
 * access React Hooks/Context from inside the Router.
 */
export function AuthWrapper({ children }: { children: React.ReactNode }) {
  const client = getAuthClient()
  const { data, error, isPending } = useGetAuthSession(client, {
    query: {
      // The rate-limited page below already explains the error.
      meta: { shouldDisplayErrorToast: false },
      // Never retry on 401 or 429 but keep trying otherwise
      retry: (_, error) => {
        return !isAuthenticationResponse(error) && !isRateLimitedError(error)
      },
      staleTime: 0,
      // Override the Query Function to immediately update the store, ensuring
      // that the Router has the authentication flag before it's loaded.
      queryFn: ({ signal }) => {
        return getAuthSession(client, { signal })
          .then((session) => {
            // Redirect the User if their session indicates so (e.g. Admin user).
            if (session.data.user.redirect) {
              redirectTo(session.data.user.redirect)
              return session
            }

            // Identify User to Mixpanel
            mixpanel.identify(session.data.user.id)

            store.setState({
              isAuthenticated: session.meta.is_authenticated,
              userRole: session.data.user.role,
            })
            return session
          })
          .catch((error) => {
            if (isAuthenticationResponse(error)) {
              store.setState({ isAuthenticated: false })
            }
            throw error
          })
      },
    },
  })

  if (isPending) {
    return <PendingComponent />
  }

  // Without a session, whether the user is logged in is unknown, so rendering
  // the app would treat them as logged out and send them to the login page.
  if (!data && error && isRateLimitedError(error)) {
    return <GenericErrorComponent error={error} />
  }

  return <>{children}</>
}
